import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, IconButton, Chip } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Dropzone from 'react-dropzone';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeDropzone — File upload dropzone.
 * Used by: Email (attachments), Document Editor (.docx upload), Bulk Assessment (spreadsheet).
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const DropArea = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDragActive',
})(({ theme, isDragActive }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '16px 12px',
  border: `1px dashed ${isDragActive ? theme.palette.text.primary : theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: isDragActive
    ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f0f4ff')
    : theme.palette.background.default,
  cursor: 'pointer',
  transition: 'border-color 0.15s, background-color 0.15s',
  '&:hover': {
    borderColor: theme.palette.text.secondary,
  },
}));

const DropText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const FileList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginTop: 6,
});

const FileItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadiusSm,
  backgroundColor: theme.palette.background.paper,
}));

const FileName = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.primary,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const NodeDropzone = React.memo(function NodeDropzone({
  label,
  onDrop,
  files = [],
  onRemoveFile,
  accept,
  maxFiles,
  maxSize,
  multiple = true,
  dropText = 'Drag & drop files here, or click to browse',
  sx,
}) {
  const [rejectionError, setRejectionError] = useState(null);

  const handleDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      const firstError = rejectedFiles[0]?.errors?.[0];
      if (firstError?.code === 'file-invalid-type') {
        const allowed = typeof accept === 'string' ? accept : '';
        setRejectionError(allowed ? `Only ${allowed} files are accepted` : 'Invalid file type');
      } else if (firstError?.code === 'file-too-large') {
        setRejectionError('File is too large');
      } else if (firstError?.code === 'too-many-files') {
        setRejectionError('Too many files');
      } else {
        setRejectionError(firstError?.message || 'File rejected');
      }
      return;
    }
    setRejectionError(null);
    onDrop?.(acceptedFiles);
  }, [onDrop, accept]);

  const handleRemove = useCallback((index, e) => {
    stopPropagation(e);
    onRemoveFile?.(index);
  }, [onRemoveFile]);

  return (
    <div onMouseDown={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <Dropzone
        onDrop={handleDrop}
        accept={accept}
        maxFiles={maxFiles}
        maxSize={maxSize}
        multiple={multiple}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <DropArea {...getRootProps()} isDragActive={isDragActive} sx={sx}>
            <input {...getInputProps()} />
            <CloudUploadOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
            <DropText>{dropText}</DropText>
          </DropArea>
        )}
      </Dropzone>
      {rejectionError && (
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.caption,
            color: 'error.main',
            mt: '4px',
            px: '4px',
          }}
        >
          {rejectionError}
        </Typography>
      )}
      {files.length > 0 && (
        <FileList>
          {files.map((file, index) => (
            <FileItem key={file.name || index}>
              <FileName>{file.name || file}</FileName>
              {onRemoveFile && (
                <IconButton size="small" onClick={(e) => handleRemove(index, e)} sx={{ p: '2px' }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </IconButton>
              )}
            </FileItem>
          ))}
        </FileList>
      )}
    </div>
  );
});

export default NodeDropzone;
